<template>
  <div class="imp-wrap" :class="{ open: dropOpen }" ref="impWrap">

    <!-- Trigger chip -->
    <div class="imp-chip" @click="dropOpen = !dropOpen" v-b-tooltip.bottom="'Impersonated session'">
      <span class="imp-pulse"></span>
      <span class="imp-avatar">{{ (currentUser.i.name || '?').charAt(0).toUpperCase() }}</span>
      <span class="imp-name">{{ currentUser.i.name }}</span>
      <i class="fa fa-chevron-down imp-caret" :class="{ rotated: dropOpen }"></i>
    </div>

    <!-- Dropdown panel -->
    <div class="imp-panel" v-if="dropOpen">
      <div class="imp-panel-head">
        <i class="fa fa-user-secret"></i>
        Impersonated Session
      </div>

      <div class="imp-user-card">
        <div class="imp-user-avatar">{{ (currentUser.i.name || '?').charAt(0).toUpperCase() }}</div>
        <div class="imp-user-info">
          <div class="imp-user-name">{{ currentUser.i.name }}</div>
          <div class="imp-user-sub">Currently viewing as this user</div>
        </div>
      </div>

      <div class="imp-divider"></div>

      <div class="imp-action" @click="openRevertModal">
        <span class="imp-action-icon blue"><i class="fa fa-arrow-left"></i></span>
        <span>Back to <strong>{{ currentUser.u.name }}</strong></span>
      </div>

      <div class="imp-action imp-action-danger" @click="logoutOfAll">
        <span class="imp-action-icon red"><i class="fa fa-sign-out-alt"></i></span>
        <span>Logout completely</span>
      </div>
    </div>

    <!-- ── Exit Impersonation Modal ── -->
    <portal to="modals">
    <div class="revert-overlay" v-if="showRevertModal" @click.self="showRevertModal = false">
      <div class="revert-card">

        <!-- Icon -->
        <div class="revert-icon-wrap">
          <div class="revert-icon">
            <i class="fa fa-user-shield"></i>
          </div>
        </div>

        <!-- Title -->
        <div class="revert-title">Exit Impersonation</div>
        <div class="revert-sub">You will be returned to your admin account</div>

        <!-- Switch visual: impersonated → admin -->
        <div class="revert-switch">
          <div class="revert-user">
            <div class="revert-user-avatar imp-color">
              {{ (currentUser.i.name || '?').charAt(0).toUpperCase() }}
            </div>
            <div class="revert-user-label">{{ currentUser.i.name }}</div>
            <div class="revert-user-role">Impersonated</div>
          </div>

          <div class="revert-arrow">
            <i class="fa fa-long-arrow-alt-right"></i>
          </div>

          <div class="revert-user">
            <div class="revert-user-avatar admin-color">
              {{ (currentUser.u.name || '?').charAt(0).toUpperCase() }}
            </div>
            <div class="revert-user-label">{{ currentUser.u.name }}</div>
            <div class="revert-user-role">Your account</div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="revert-footer">
          <button class="btn-revert-cancel" @click="showRevertModal = false" :disabled="reverting">
            Cancel
          </button>
          <button class="btn-revert-confirm" @click="confirmRevert" :disabled="reverting">
            <i v-if="reverting" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-arrow-left"></i>
            {{ reverting ? 'Returning…' : 'Go Back' }}
          </button>
        </div>

      </div>
    </div>
    </portal>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { REVERT_TO_USER, LOGOUT } from "@/core/services/store/actions.type";

export default {
  name: "Impersonator",
  computed: {
    ...mapGetters(["currentUser"])
  },
  data() {
    return {
      dropOpen: false,
      showRevertModal: false,
      reverting: false
    };
  },
  created() {
    document.addEventListener("click", this.onOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutsideClick);
  },
  methods: {
    onOutsideClick(e) {
      if (this.$refs.impWrap && !this.$refs.impWrap.contains(e.target)) {
        this.dropOpen = false;
      }
    },
    openRevertModal() {
      this.dropOpen = false;
      this.showRevertModal = true;
    },
    confirmRevert() {
      this.reverting = true;
      window.localStorage.removeItem("search");
      window.localStorage.removeItem("appoitmentsearch");
      window.localStorage.removeItem("showNotificationModal");
      this.$store.dispatch(REVERT_TO_USER).then(() => {
        this.$router.push({ name: "admin.dashboard" }).then(() => {
          window.location.reload();
        });
      });
    },
    logoutOfAll() {
      this.dropOpen = false;
      this.$store.dispatch(LOGOUT).then(() => {
        window.localStorage.removeItem("appoitmentsearch");
        window.localStorage.removeItem("showNotificationModal");
        this.$router.push({
          name: "client.login",
          params: { client_slug: this.$route.params.client_slug }
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$blue: #389ad4;
$dark: #0d1b3e;
$red: #e53e3e;
$font: 'Segoe UI', sans-serif;

.imp-wrap {
  position: relative;
  font-family: $font;
  margin-right: 6px;
}

/* ── Trigger chip ── */
.imp-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 12px 0 10px;
  background: rgba(229, 62, 62, 0.75);
  border: 1.5px solid rgba(229, 62, 62, 0.9);
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  user-select: none;

  &:hover {
    background: rgba(229, 62, 62, 0.18);
    border-color: rgba(229, 62, 62, 0.55);
  }

  .open & {
    background: rgba(229, 62, 62, 0.2);
    border-color: rgba(229, 62, 62, 0.6);
  }
}

.imp-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $red;
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: rgba(229, 62, 62, 0.4);
    animation: pulse 1.6s ease-out infinite;
  }
}

@keyframes pulse {
  0%   { transform: scale(1);   opacity: 0.7; }
  70%  { transform: scale(1.9); opacity: 0; }
  100% { transform: scale(1.9); opacity: 0; }
}

.imp-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $red;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.imp-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #fff;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.imp-caret {
  font-size: 10px;
  color: #fff;
  transition: transform 0.2s;

  &.rotated { transform: rotate(180deg); }
}

/* ── Dropdown panel ── */
.imp-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  min-width: 240px;
  box-shadow: 0 12px 32px rgba(13, 27, 62, 0.14);
  z-index: 9999;
  overflow: hidden;
}

.imp-panel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  background: #fff5f5;
  border-bottom: 1px solid #fed7d7;
  font-size: 11.5px;
  font-weight: 700;
  color: #c53030;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  i { font-size: 13px; }
}

.imp-user-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
}

.imp-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c53030, #e53e3e);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.imp-user-info { min-width: 0; }

.imp-user-name {
  font-size: 13px;
  font-weight: 700;
  color: $dark;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.imp-user-sub {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.imp-divider {
  height: 1px;
  background: #f0f3fa;
  margin: 0 16px;
}

.imp-action {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 13px;
  color: #2d3748;
  cursor: pointer;
  transition: background 0.15s;

  strong { font-weight: 600; color: $dark; }

  &:hover { background: #f8fafc; }

  &.imp-action-danger {
    color: $red;
    strong { color: $red; }
    &:hover { background: #fff5f5; }
  }
}

.imp-action-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;

  &.blue { background: #ebf5fb; color: $blue; }
  &.red  { background: #fff5f5; color: $red; }
}

/* ── Revert confirm modal (non-scoped — see block below) ── */
</style>

<style lang="scss">
/* Impersonator revert modal — non-scoped so portal target can render it */
.revert-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(3px);
  padding: 20px;
  font-family: 'Segoe UI', sans-serif;
}

.revert-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 64px rgba(13, 27, 62, 0.22);
  width: 100%;
  max-width: 400px;
  padding: 32px 28px 28px;
  text-align: center;
  font-family: 'Segoe UI', sans-serif;
}

.revert-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.revert-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1E6FB8, #389ad4);
  color: #fff;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(56, 154, 212, 0.35);
}

.revert-title {
  font-size: 16px;
  font-weight: 700;
  color: #0d1b3e;
  margin-bottom: 6px;
}

.revert-sub {
  font-size: 13px;
  color: #7a8499;
  margin-bottom: 24px;
  line-height: 1.5;
}

.revert-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #f8fafd;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.revert-user {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex: 1;
}

.revert-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;

  &.imp-color {
    background: linear-gradient(135deg, #c53030, #e53e3e);
    opacity: 0.55;
  }

  &.admin-color {
    background: linear-gradient(135deg, #1E6FB8, #389ad4);
  }
}

.revert-user-label {
  font-size: 12px;
  font-weight: 600;
  color: #0d1b3e;
  text-align: center;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.revert-user-role {
  font-size: 10.5px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.revert-arrow {
  font-size: 20px;
  color: #389ad4;
  flex-shrink: 0;
}

.revert-footer {
  display: flex;
  gap: 10px;

  button {
    flex: 1;
    height: 42px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-family: 'Segoe UI', sans-serif;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.btn-revert-cancel {
  background: #fff;
  color: #4a5568;
  border: 1.5px solid #dde2ec;

  &:hover:not(:disabled) {
    border-color: #a0aab8;
    color: #2d3748;
  }
}

.btn-revert-confirm {
  background: #389ad4;
  color: #fff;
  border: 1.5px solid #389ad4;

  &:hover:not(:disabled) {
    background: #2a85be;
    border-color: #2a85be;
  }
}
</style>
